import { useCallback } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  listParamsOf,
  listSearchSchema,
  nextListSearch,
  type ListParams,
  type ListSearch,
} from './url-state';

/**
 * A list screen's state, read from and written to the URL (SDD-017 §9, L-12).
 *
 * The route validates its search with `listSearchSchema` (extended by its own keys); this hook
 * reads the current location — so it works on any route, and in a story under the memory router —
 * and writes a change back with `nextListSearch`, as a new history entry: the back button undoes a
 * filter. Screens never hold filter, search, order or business date in component state.
 */
export interface ListUrlState {
  /** The location's whole search (a route's own keys included), as the router parsed it. */
  readonly raw: Readonly<Record<string, unknown>>;
  /** The list keys, validated. */
  readonly search: ListSearch;
  /** The list keys that say something — what goes into the request. */
  readonly params: ListParams;
  /** Merges `patch` into the URL; an emptied key leaves the URL. */
  readonly set: (patch: Partial<ListSearch> & Readonly<Record<string, unknown>>) => void;
}

export function useListSearch(): ListUrlState {
  const navigate = useNavigate();
  const raw = useRouterState({
    select: (state) => state.location.search as Readonly<Record<string, unknown>>,
  });
  const parsed = listSearchSchema.safeParse(raw);
  const search: ListSearch = parsed.success ? parsed.data : {};
  const set = useCallback(
    (patch: Partial<ListSearch> & Readonly<Record<string, unknown>>) => {
      void navigate({
        to: '.',
        search: (previous: Record<string, unknown>) => nextListSearch(previous, patch),
      });
    },
    [navigate],
  );
  return { raw, search, params: listParamsOf(search), set };
}
